/**
 * F1 (#1806): the client's twin of the server's persisted-words rule (`agent/landing/v1/thread_facts.py`:
 * `persisted_all`, `preview_of`). Wherever the client stores a thread beyond the screen (the snapshot it upserts, the
 * localStorage buffer a failed upsert leaves, the parked chat snapshots), a Writer answer is stored as the server stores it: its record's
 * references and the model's numberless words, every rendered field (headline, narrative, highlights, tiles, the
 * Writer's follow-ups, the formatters' words) removed, a turn's streamed words dropped, a request's replies as the
 * placeholder, a walk's rendered earlier line taken out of its caveats, and a Writer turn's preview its question. The
 * words are rendered for the reader when the server reads the thread for them; the in-memory working copy stays
 * rendered (F4's restore path), and a turn restored from a stored copy is a placeholder the server's read takes the
 * place of (`writerWordsStripped`, `storedTurn.needsRender`). Held to the server's own output by
 * `__fixtures__/writerPersisted.json` (written by `tests/test_data_writer_1806.py`).
 */

export const HISTORY_PLACEHOLDER = '[an earlier answer]';
export const WHY_LINE_LEAD = 'The earlier answer showed ';
export const STREAMED_TURN_KEYS = ['streaming', 'stoppedWords'];
export const SNAPSHOT_KEYS = ['formatterSummary', 'formatterHighlights', 'formatterTitle'];
/** The fields a Writer record projects (`thread_facts.PROJECTED`), each emptied wherever a copy is stored. */
export const LADDER_FIELDS = ['what.headline', 'what.narrative', 'what.highlights', 'what.tiles', 'next[writer]'];
export const SYNTHESIS_FIELDS = ['headline', 'narrative', 'highlights', 'follow_ups', 'key_numbers'];
export const THREAD_FIELDS = ['preview'];
// what an emptied field holds (the server's `persisted`): words none, lists empty
const EMPTY: Record<string, null | []> = { headline: null, narrative: null, highlights: [], tiles: [], follow_ups: [],
  key_numbers: [] };
const emptied = (fields: string[]) => Object.fromEntries(fields.map((f) => [f, EMPTY[f]]));
const WHAT_FIELDS = LADDER_FIELDS.filter((f) => f.startsWith('what.')).map((f) => f.slice('what.'.length));
const WRITER_METHOD = 'writer';

const isObj = (v: unknown): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): v is string => typeof v === 'string';

/** A data answer's Writer record (`thread_facts.is_record`). */
export function isRecord(v: unknown): v is Record<string, any> {
  return isObj(v) && v.kind === 'data' && Array.isArray(v.supplied) && Array.isArray(v.headline);
}

const ladderRecord = (v: unknown) => (isObj(v) && isObj(v.what) && isRecord(v.what.writer) ? v.what.writer : null);
const synthesisRecord = (v: unknown) => (isObj(v) && isRecord(v.writer) && 'headline' in v && !('tables' in v)
  ? v.writer : null);
const isAskRequest = (v: unknown) => isObj(v) && str(v.query) && Array.isArray(v.history);
const isWhyLine = (v: unknown) => isObj(v) && v.kind === 'why_earlier' && str(v.earlier) && str(v.claim);

function parts(seq: unknown): Record<string, string>[] {
  const out: Record<string, string>[] = [];
  for (const p of Array.isArray(seq) ? seq : []) {
    if (isObj(p) && str(p.cite)) out.push({ cite: p.cite });
    else if (isObj(p) && str(p.text)) out.push({ text: p.text });
  }
  return out;
}

/** A record reduced to what it may store (`thread_facts.kept_record`). */
export function keptRecord(rec: Record<string, any>): Record<string, any> {
  return {
    kind: 'data', session_id: str(rec.session_id) ? rec.session_id : null,
    written_by: rec.written_by === 'model' || rec.written_by === 'code' ? rec.written_by : 'code',
    supplied: (rec.supplied || []).filter(str), headline: parts(rec.headline), summary: parts(rec.summary),
    highlights: (Array.isArray(rec.highlights) ? rec.highlights : []).filter(Array.isArray).map(parts),
    follow_ups: (Array.isArray(rec.follow_ups) ? rec.follow_ups : []).filter(str),
    key: (Array.isArray(rec.key) ? rec.key : []).filter(str), earlier: str(rec.earlier) ? rec.earlier : null,
  };
}

const writerSuggestion = (s: unknown) => isObj(s) && s.kind === 'follow_up' && Array.isArray(s.sources)
  && s.sources.some((src: any) => isObj(src) && (src.method === WRITER_METHOD
    || (isObj(src.meta) && src.meta.method === WRITER_METHOD)));

/** One value's own fields as they may be stored (`thread_facts.persisted`). */
export function persisted(value: any): any {
  if (!isObj(value)) return value;
  let out: Record<string, any> = {};
  for (const [k, v] of Object.entries(value)) if (!STREAMED_TURN_KEYS.includes(k)) out[k] = v;
  if (isAskRequest(out)) {
    out.history = out.history.filter(isObj).map((h: any) => (h.role === 'user' ? h : { ...h, content: HISTORY_PLACEHOLDER }));
  }
  const rec = ladderRecord(out);
  if (rec) {
    out.what = { ...out.what, ...emptied(WHAT_FIELDS), writer: keptRecord(rec) };
    if (LADDER_FIELDS.includes('next[writer]') && Array.isArray(out.next)) {
      const kept = out.next.filter((s: any) => !writerSuggestion(s));
      out.next = kept.length ? kept : null;
    }
  }
  const srec = synthesisRecord(out);
  if (srec) {
    out = { ...out, ...emptied(SYNTHESIS_FIELDS), writer: keptRecord(srec) };
  }
  if (synthesisRecord(out.synthesis)) for (const k of SNAPSHOT_KEYS) delete out[k];
  if (isWhyLine(out.earlier_line) && Array.isArray(out.caveats)) {
    out.caveats = out.caveats.filter((c: any) => !(str(c) && c.startsWith(WHY_LINE_LEAD)));
  }
  return out;
}

/** `persisted` at every level (`thread_facts.persisted_all`). */
export function writerPersisted<T>(value: T): T {
  if (Array.isArray(value)) return value.map(writerPersisted) as unknown as T;
  if (!isObj(value)) return value;
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(persisted(value))) out[k] = writerPersisted(v);
  return out as T;
}

/** A thread's preview as it may be stored (`thread_facts.preview_of`): a Writer turn's question, never its words. */
export function previewOf(turns: unknown, preview: unknown): unknown {
  const last = Array.isArray(turns) && turns.length && isObj(turns[turns.length - 1]) ? turns[turns.length - 1] : null;
  const result = last && isObj(last.result) ? last.result : {};
  const written = !!synthesisRecord(result.synthesis) || !!ladderRecord(result.ladder)
    || (!!last && STREAMED_TURN_KEYS.some((k) => k in last));
  if (!written) return preview;
  return last && str(last.userText) ? last.userText.slice(0, 240) : '';
}

/** Whether a stored turn holds a Writer answer whose words were taken out — rendered only by the server's read. */
export function writerWordsStripped(turn: any): boolean {
  const res = turn && isObj(turn.result) ? turn.result : null;
  if (!res) return false;
  const syn = synthesisRecord(res.synthesis) ? res.synthesis : null;
  const what = ladderRecord(res.ladder) ? res.ladder.what : null;
  return (!!syn && syn.headline == null) || (!!what && what.headline == null);
}
